import type {Icon as PrimerIcon} from '@primer/octicons-react'
import {ClockHandsIcon} from '@github-ui/custom-icons/ClockHandsIcon'
import {ForYouChangesRequestedIcon} from '@github-ui/custom-icons/ForYouChangesRequestedIcon'
import {MergeabilityFailureIcon} from '@github-ui/custom-icons/MergeabilityFailureIcon'
import {ForYouItem} from '../shared/ForYouItem'
import type {ForYouItemTone} from '../shared/for-you-item.types'
import {useMergeabilityData} from './use-mergeability-data'
import {decideReviewSubmittedItem, type ReviewSubmittedItemKind} from './shared/review-submitted-item-decider'
import {OverviewMergeControl} from './shared/OverviewMergeControl'
import {useUnresolvedConversationCount} from '../../../../page-data/loaders/use-tab-counts'
import {OverviewUpdateBranchControl} from './shared/OverviewUpdateBranchControl'
import {isForYouRefreshEnabled} from '../shared/for-you-feature-flags'
import {useForYouActionErrors} from '../shared/use-for-you-action-errors'

type ReviewSubmittedVariantConfig = {
  icon: PrimerIcon
  tone: ForYouItemTone
  glyphSize: 12 | 16 | 24
  title: string
  description: string
  itemName: string
}

const VARIANT_CONFIG: Record<
  Exclude<ReviewSubmittedItemKind, 'none' | 'address-comments'>,
  ReviewSubmittedVariantConfig
> = {
  'required-changes-requested': {
    icon: ForYouChangesRequestedIcon,
    tone: 'danger',
    glyphSize: 16,
    title: 'Changes requested',
    description: 'Address the requested changes before merging.',
    itemName: 'required-changes-requested',
  },
  'comments-resolution-required': {
    icon: MergeabilityFailureIcon,
    tone: 'danger',
    glyphSize: 12,
    title: 'Unresolved comments must be resolved',
    description: 'Resolve all comments before merging.',
    itemName: 'comments-resolution-required',
  },
  'approval-required': {
    icon: ClockHandsIcon,
    tone: 'attention',
    glyphSize: 24,
    title: 'Approval required',
    description: 'Request another approving review before this can merge.',
    itemName: 'approval-required',
  },
}

// Author-facing item that summarizes the state after a review has been submitted.
// Renders one of four mutually exclusive states, prioritized by the rubric:
// required-changes-requested > comments-resolution-required > approval-required.
// Address-comments is intentionally omitted
export function ReviewSubmitted() {
  const {onUpdateBranchError, onQueueError, footer} = useForYouActionErrors()
  const data = useMergeabilityData()
  const unresolvedThreadCount = useUnresolvedConversationCount() ?? 0

  const decision = decideReviewSubmittedItem(data, unresolvedThreadCount > 0)
  if (decision.kind === 'none' || decision.kind === 'address-comments') return null

  const config = VARIANT_CONFIG[decision.kind]
  return (
    <ForYouItem
      itemId={config.itemName}
      footer={footer}
      visual={{kind: 'icon', icon: config.icon, tone: config.tone, glyphSize: config.glyphSize}}
      title={config.title}
      subText={config.description}
      actions={[
        {
          id: 'update-branch',
          content: isForYouRefreshEnabled() ? (
            <OverviewUpdateBranchControl itemName={config.itemName} onError={onUpdateBranchError} />
          ) : null,
        },
        {
          id: 'merge-when-ready',
          content: <OverviewMergeControl data={data} itemName={config.itemName} onQueueError={onQueueError} />,
        },
      ]}
    />
  )
}
